import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useCancelInvoiceReport } from '@/features/invoice_reports/components/ConfirmCancelModal/hooks/useCancelInvoiceReport';
import { useTranslation } from '@/i18n';
import {
  ButtonV2Props,
  ModalWithoutPortal,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import styles from './ConfirmCancelModal.module.scss';
import { useConfirmCancelModal } from './context/ConfirmCancelModalContext';

const cx = classnames.bind(styles);

export const ConfirmCancelModal: FC = memo(() => {
  const {
    cancelModalOpen,
    cancelRecord,
    onCloseConfirmCancelModal,
    refetch,
    onDrawerClose,
    selectedId,
  } = useConfirmCancelModal();

  const { t } = useTranslation();
  const { cancelReport } = useCancelInvoiceReport({
    refetch,
    onDrawerClose,
    selectedId,
  });

  const handleCancel = useCallback(async () => {
    if (!cancelRecord) return;
    await cancelReport(cancelRecord.id)();
    onCloseConfirmCancelModal();
  }, [cancelReport, cancelRecord, onCloseConfirmCancelModal]);

  const closeButtonProps: ButtonV2Props = useMemo(
    () => ({
      isTertiary: true,
      onClick: onCloseConfirmCancelModal,
      size: 'md',
      children: t('cancel'),
    }),
    [onCloseConfirmCancelModal, t]
  );

  const confirmButtonProps: ButtonV2Props = useMemo(
    () => ({
      color: 'danger',
      onClick: handleCancel,
      size: 'md',
      children: t('ap_report_status_canceled'),
    }),
    [handleCancel, t]
  );

  return (
    <ModalWithoutPortal
      open={cancelModalOpen}
      onClose={onCloseConfirmCancelModal}
      size='md'
      title={t('ap_report_status_canceled')}
      mask
      footer={
        <ConfirmationModalFooter
          cancelButton={closeButtonProps}
          okButton={confirmButtonProps}
        />
      }
    >
      <div>
        <p className={cx('confirm-message')}>
          {t('invoice_reports.action_menu.cancel_confirm_message')}
        </p>
        {cancelRecord && (
          <div className={cx('detail-table')}>
            {cancelRecord.number && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.application_number')}
                </span>
                <span className={cx('detail-value')}>
                  {cancelRecord.number}
                </span>
              </div>
            )}
            {cancelRecord.title && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.title')}
                </span>
                <span className={cx('detail-value')}>{cancelRecord.title}</span>
              </div>
            )}
            {cancelRecord.apPayeeName && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.payee')}
                </span>
                <span className={cx('detail-value')}>
                  {cancelRecord.apPayeeName}
                </span>
              </div>
            )}
            {cancelRecord.totalAmount && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.total_amount')}
                </span>
                <span className={cx('detail-value')}>
                  {cancelRecord.totalAmount}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalWithoutPortal>
  );
});

ConfirmCancelModal.displayName = 'ConfirmCancelModal';
